// useHomeConnect.js - Estado e controlo dos aparelhos Bosch (Home Connect)
//
// Duas vistas usam esta API: a máquina de loiça e o exaustor. Cada uma pede
// só o seu tipo de aparelho — a API da Bosch tem um rate limit apertado, e
// pedir os dois quando só se vê um foi o que nos valeu uma série de 429.
//
// Duas defesas contra esse limite, ambas aprendidas à força:
//   - o intervalo é de 90s, não de 15s como no Tuya;
//   - depois de uma ação, o estado é atualizado localmente em vez de se
//     voltar a perguntar (já sabemos o valor que mandámos aplicar), e há um
//     período de 4s em que não se aceita outra ação.
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = '/api';
const INTERVALO_MS = 90000;
const ESPERA_MS = 4000;

// Os níveis de extração do exaustor, por ordem: desligado e três velocidades.
export const NIVEIS_EXTRACAO = [
  'Cooking.Hood.EnumType.Stage.FanOff',
  'Cooking.Hood.EnumType.Stage.FanStage01',
  'Cooking.Hood.EnumType.Stage.FanStage02',
  'Cooking.Hood.EnumType.Stage.FanStage03',
];

function comAutorizacao(extra = {}) {
  const token = localStorage.getItem('access_token');
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export function useHomeConnect(applianceType) {
  const { t } = useTranslation();
  const [appliances, setAppliances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emEspera, setEmEspera] = useState(false);

  const fetchAppliances = useCallback(async () => {
    try {
      const qs = applianceType ? `?appliance_type=${applianceType}` : '';
      const response = await fetch(`${API_URL}/homeconnect/appliances${qs}`, {
        headers: comAutorizacao(),
      });
      const data = await response.json();
      // O corpo de erro traz `detail`, não `appliances`: sem esta verificação
      // a vista ficava presa no "a carregar" para sempre.
      if (!response.ok) {
        setError(data.detail || t('erros.buscarHomeConnect'));
        return;
      }
      if (data.appliances) setAppliances(data.appliances);
    } catch (err) {
      setError(t('erros.buscarHomeConnect'));
    }
  }, [applianceType, t]);

  // Atualiza um campo localmente, sem voltar a consultar a Bosch.
  const atualizarCampo = useCallback((haId, lista, key, value) => {
    setAppliances((prev) => prev.map((a) => {
      if (a.ha_id !== haId) return a;
      const items = a[lista] || [];
      const idx = items.findIndex((i) => i.key === key);
      const novos = idx >= 0
        ? items.map((it, i) => (i === idx ? { ...it, value } : it))
        : [...items, { key, value }];
      return { ...a, [lista]: novos };
    }));
  }, []);

  const enviar = useCallback(async (url, corpo, aoSucesso, erroPadrao) => {
    if (emEspera) return;
    try {
      setLoading(true);
      setEmEspera(true);
      const response = await fetch(url, {
        method: 'PUT',
        headers: comAutorizacao({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(corpo),
      });
      const data = await response.json();
      if (data.success) aoSucesso();
      else setError(data.detail || erroPadrao);
    } catch (err) {
      setError(t('erros.enviarComando'));
    } finally {
      setLoading(false);
      setTimeout(() => setEmEspera(false), ESPERA_MS);
    }
  }, [emEspera, t]);

  const definirSetting = useCallback((haId, key, value) => enviar(
    `${API_URL}/homeconnect/${haId}/settings`,
    { key, value },
    () => atualizarCampo(haId, 'settings', key, value),
    t('erros.controlarAparelho'),
  ), [enviar, atualizarCampo, t]);

  // A extração do exaustor não é uma definição, é um *programa*: pede-se um
  // "Hood.Venting" com o nível como opção. Levou algum tempo a descobrir.
  const definirExtracao = useCallback((haId, level) => enviar(
    `${API_URL}/homeconnect/${haId}/hood/venting`,
    { level, intensive: 0 },
    () => atualizarCampo(haId, 'active_program_options', 'Cooking.Common.Option.Hood.VentingLevel', NIVEIS_EXTRACAO[level]),
    t('erros.controlarExaustor'),
  ), [enviar, atualizarCampo, t]);

  useEffect(() => {
    fetchAppliances();
    const interval = setInterval(fetchAppliances, INTERVALO_MS);
    return () => clearInterval(interval);
  }, [fetchAppliances]);

  return {
    appliances, loading, error, emEspera,
    fetchAppliances, definirSetting, definirExtracao,
  };
}

// Lê um valor do aparelho. As leituras vivem em `status`, o que se pode
// mudar em `settings` e as opções do programa a correr em
// `active_program_options` — daí a lista ser um parâmetro.
export function valorDe(appliance, key, lista = 'status') {
  const item = (appliance?.[lista] || []).find((s) => s.key === key);
  return item ? item.value : undefined;
}
